import React, { useState, useCallback, useEffect, useRef } from 'react';
import { triggerPiepeline, PipelineRun, uploadCustomFile, cancelPipeline } from '../../services/api/virtualAssistant'; 
import { Pipeline } from "../../types/pipelineData";
import CustomModal from "../../components/CustomModal";
import WarningModal from "../../components/WarningModal";

// Import child components
import FileDropzone from './FileDropzone';
import FileList from './FileList';
import DataProcessingControls from './DataProcessingControls';

// Import types
import { 
  FileUploaderProps, 
  UploadedFile, 
  SystemConfig, 
  PipelineStatus,
  CustomModalState 
} from './types';

const FileUploader: React.FC<FileUploaderProps> = ({ 
  assistant, 
  onUploadComplete, 
  triggerAutoSave,
  isCustomFileRNightly,
  setIsCustomFileRNightly,
  isCustomFileFullLoad,
  setIsCustomFileFullLoad 
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>(assistant?.data_uploaded_files || []);
  const [systemConfig, setSystemConfig] = useState<SystemConfig[]>([]);
  const [pipelineData, setPipelineData] = useState<Pipeline[]>([]);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const [gettingData, setGettingData] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<PipelineStatus | null>(null);
  const [isGettingDataCalled, setIsGetDataCallled] = useState(true);
  const [getDataClickTime, setGetDataClickTime] = useState<Date | null>(null);
  const [modalRefreshNModal, setModalRefreshNModal] = useState(false);
  const [pendingSave, setPendingSave] = useState(false);
  const [expandedGetData, setExpandedGetData] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const [cancellingPipeline, setCancellingPipeline] = useState(false);
  
  const [customModal, setCustomModal] = useState<CustomModalState>({
    isOpen: false,
    title: "",
    message: "",
    type: "confirmation",
    allowClose: true,
  });

  const expandedSectionRef = useRef<HTMLDivElement | null>(null);

  // Load system configuration from sessionStorage
  useEffect(() => {
    const getCachedData = (key: string) => {
      const item = sessionStorage.getItem(key);
      if (!item) return null;
      const { value, timestamp } = JSON.parse(item);
      if (Date.now() - timestamp > 2 * 24 * 60 * 60 * 1000) { // 2 days cache
        sessionStorage.removeItem(key);
        return null;
      }
      return value;
    };

    const config = getCachedData('systemConfig');
    if (config) {
      setSystemConfig(config);
    }
  }, []);

  // Initialize uploaded files from assistant data
  useEffect(() => {
    if (assistant?.app_id && uploadedFiles.length === 0) {
      const initialFiles: UploadedFile[] = assistant?.data_uploaded_files || [];
      const updatedFiles: UploadedFile[] = initialFiles.map((file: UploadedFile) => ({
        ...file,
        status: "completed" as "completed",
        size: file.size ?? 0,
      }));
      setUploadedFiles(updatedFiles);
    }
  }, [assistant]);

  // Notify parent component when uploadedFiles change
  useEffect(() => {
    onUploadComplete([...uploadedFiles]);
  }, [uploadedFiles, onUploadComplete]);

  // Scroll to expanded section when expandedGetData becomes true
  useEffect(() => {
    if (expandedGetData && expandedSectionRef.current) {
      expandedSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [expandedGetData]);

  // Pipeline data fetching and status management
  useEffect(() => {
    fetchPipelineData();
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  
    if (currentStatus?.status !== 'Complete' && currentStatus?.status !== 'Cancelled') {
      if (isGettingDataCalled) {
        intervalRef.current = setInterval(() => {
          fetchPipelineData();
        }, 10 * 1000); // 10 seconds
      }
    }
  
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [currentStatus?.status, isGettingDataCalled]);

  const getFileCategory = (extension: string): string => {
    const imageExtensions = ["png", "jpg", "jpeg", "bmp", "gif", "tiff", "heif"];
    const pdfExtensions = ["pdf"];
    const documentExtensions = ["doc", "docx", "txt", "rtf"];
    const spreadsheetExtensions = ["xls", "xlsx", "csv"];
    const presentationExtensions = ["ppt", "pptx"];
    const videoExtensions = ["mp4", "mov", "avi", "mkv", "flv", "wmv"];
  
    if (imageExtensions.includes(extension)) return "image";
    if (pdfExtensions.includes(extension)) return "pdf";
    if (documentExtensions.includes(extension)) return "doc";
    if (spreadsheetExtensions.includes(extension)) return "spreadsheet";
    if (presentationExtensions.includes(extension)) return "presentation";
    if (videoExtensions.includes(extension)) return "video";
  
    return "other";
  };

  const handleFilesDropped = useCallback(async (acceptedFiles: File[]) => {
    const maxSequence = uploadedFiles.length > 0 ? Math.max(...uploadedFiles.map(file => parseInt(file.sequence))) : 0;
  
    const newFiles: UploadedFile[] = acceptedFiles.map((file, index) => {
      const extension = file.name.split(".").pop()?.toLowerCase() || "";
      return {
        file,
        id: Math.random().toString(36).substr(2, 9),
        status: "uploading",
        progress: 0,
        file_name: file.name,
        size: file.size,
        error: null,
        location: null,
        sequence: (maxSequence + index + 1).toString(),
        file_url: "",
        file_type: getFileCategory(extension),
        file_extn: extension ? `.${extension}` : "",
      };
    });
  
    setUploadedFiles(prev => [...prev, ...newFiles]);
  
    // Upload each file
    newFiles.forEach(async (fileObj) => {
      try {
        const { fileName, fileLocation, file_unique_id } = await uploadCustomFile(
          fileObj.file, 
          'general', 
          assistant.app_id, 
          localStorage.getItem('netId') || ''
        );
        
        setUploadedFiles(prev => {
          const updatedFiles = prev.map(f => 
            f.id === fileObj.id 
              ? { 
                  ...f, 
                  status: 'completed' as 'completed', 
                  location: fileLocation, 
                  progress: 100, 
                  file_url: fileLocation, 
                  file_unique_id: file_unique_id 
                } 
              : f
          );
          return updatedFiles;
        });
        triggerAutoSave();
        setPendingSave(true);
      } catch (error) {
        setUploadedFiles(prev => prev.map(f => 
          f.id === fileObj.id 
            ? { 
                ...f, 
                status: 'error', 
                error: error instanceof Error ? error.message : 'Upload failed', 
                progress: 0 
              } 
            : f
        ));
      }
    });
  }, [uploadedFiles, triggerAutoSave, assistant.app_id]);

  const showDeleteConfirmationModal = (message: string, onConfirm: () => void) => {
    setCustomModal({
      isOpen: true,
      title: "Delete Confirmation",
      message,
      type: "confirmation",
      onConfirm,
    });
  };

  const removeFile = (fileId: string): void => {
    let warningMessage = 'Are you sure you want to delete custom file? This action cannot be undone.';
    showDeleteConfirmationModal(warningMessage, async () => {
      setCustomModal((prev) => ({
        ...prev,
        isOpen: false,
      }));
            
      setCustomModal({
        isOpen: true,
        title: "Deleting...",
        message: "Deleting file, please wait...",
        type: "loading",
        allowClose: false,
      });
    
      try {
        setUploadedFiles(prev => prev.filter(f => f.sequence !== fileId));
        triggerAutoSave();
        setTimeout(() => {
          let queue_name = systemConfig.find(item => item.Id === 'customfiles_queue_name')?.ConfigValue ?? '';
          const run_type = 'Adhoc';
          const full_load_value = String(isCustomFileFullLoad) === 'true' ? 'false' : 'true';
          if (queue_name) {
            const res = triggerPiepeline(assistant.app_id, run_type, queue_name, full_load_value);
            setPendingSave(false);
            setTimeout(() => {
              fetchPipelineData();
              setTimeout(() => {
                fetchPipelineData();
              }, 500);
            }, 500); 
            setCustomModal({
              isOpen: true,
              title: "Success!",
              message: 'Custom file deleted successfully',
              type: "success",
              allowClose: true,
            });
          }
        }, 20 * 1000);
      } catch (error) {
        console.log("Delete failed", error);
      }
    });
  };

  const fetchPipelineData = async () => {
    const pipeline_name = "customfile_to_vectordb";
    if (assistant.app_id) {
      const resPipelineRun = await PipelineRun(pipeline_name, assistant.app_id);
      setPipelineData(resPipelineRun);
      if (resPipelineRun && resPipelineRun.length > 0) {
        setIsGetDataCallled(true);

        const backendStatus = resPipelineRun[0].status;
        let status = '';

        // Map backend status to UI status - ALWAYS check actual status first
        if (backendStatus === 'STARTED') status = 'In Progress';
        if (backendStatus === 'failed') status = 'Failed';
        if (backendStatus === 'success') status = 'Complete';
        if (backendStatus === 'cancelled') {
          status = 'Cancelled';
          setCancellingPipeline(false);
          setGettingData(false);
        }
        if (backendStatus === 'cancelling') {
          status = 'Cancelling';
          setCancellingPipeline(true);
        }
        if (backendStatus === 'cancel_failed') {
          status = 'Failed';
          setCancellingPipeline(false);
        }

        // Special case: if pipeline was created before we clicked "Get Data"
        // and status is STARTED, ensure we show "In Progress" (no regression)
        const responseCreatedTs = new Date(resPipelineRun[0].created_ts);
        if (getDataClickTime && responseCreatedTs < getDataClickTime && backendStatus === 'STARTED') {
          status = 'In Progress';
        }

        setCurrentStatus({ progress: 20, status: status });
      } else {
        if (gettingData) {
          setCurrentStatus({ progress: 20, status: "In Progress" });
          setIsGetDataCallled(true);
        } else {
          setCurrentStatus({ progress: 20, status: "" });
          setIsGetDataCallled(false);
        }
      }
    }
  };

  const handleCancelPipeline = async () => {
    try {
      setCancellingPipeline(true);
      setCurrentStatus(prev => ({ ...prev, progress: 20, status: 'Cancelling' }));

      await cancelPipeline("customfile_to_vectordb", assistant.app_id);

      // Poll for status update after cancel request
      setTimeout(() => {
        fetchPipelineData();
      }, 2000);
    } catch (error) {
      console.error('Failed to cancel pipeline:', error);
      setCancellingPipeline(false);
      setCurrentStatus(prev => ({ ...prev, progress: 20, status: 'In Progress' }));
      
      setCustomModal({
        isOpen: true,
        title: "Cancel Failed",
        message: "Failed to cancel the operation. Please try again.",
        type: "error",
      });
    }
  };

  const handleGetDataNow = async () => {
    setGettingData(true);
    setPendingSave(false);
    setGetDataClickTime(new Date());  
    setCurrentStatus({ progress: 20, status: 'In Progress' });
    
    let queue_name = systemConfig.find(item => item.Id === 'customfiles_queue_name')?.ConfigValue ?? '';
    const run_type = 'Adhoc';
    const full_load_value = String(isCustomFileFullLoad) === 'true' ? 'false' : 'true';
    
    if (queue_name) {
      const res = await triggerPiepeline(assistant.app_id, run_type, queue_name, full_load_value);
      setTimeout(() => {
        fetchPipelineData();
      }, 30000); 
    }
  };

  const toggleExpandRow = (idx: number) => {
    setExpandedRows(prev => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  const handleExpandToggle = () => {
    setExpandedGetData(!expandedGetData);
  };

  return (
    <div className="w-full space-y-4 border-2 rounded-lg p-4">
      <FileDropzone 
        systemConfig={systemConfig}
        onFilesDropped={handleFilesDropped}
      />

      <FileList 
        uploadedFiles={uploadedFiles}
        onRemoveFile={removeFile}
        assistant={assistant}
      />

      <DataProcessingControls
        uploadedFiles={uploadedFiles}
        isCustomFileRNightly={isCustomFileRNightly}
        setIsCustomFileRNightly={setIsCustomFileRNightly}
        isCustomFileFullLoad={isCustomFileFullLoad}
        setIsCustomFileFullLoad={setIsCustomFileFullLoad}
        triggerAutoSave={triggerAutoSave}
        pendingSave={pendingSave}
        currentStatus={currentStatus}
        onGetDataNow={handleGetDataNow}
        onExpandToggle={handleExpandToggle}
        expandedGetData={expandedGetData}
        pipelineData={pipelineData}
        expandedRows={expandedRows}
        toggleExpandRow={toggleExpandRow}
        assistant={assistant}
        systemConfig={systemConfig}
        onCancelPipeline={handleCancelPipeline}
        cancellingPipeline={cancellingPipeline}
      />

      <CustomModal
        isOpen={customModal.isOpen}
        onClose={() => setCustomModal({ ...customModal, isOpen: false })}
        onConfirm={customModal.onConfirm}
        title={customModal.title}
        message={customModal.message}
        type={customModal.type}
        allowClose={customModal.allowClose}
      />
      
      <WarningModal
        isOpen={modalRefreshNModal}
        onClose={() => setModalRefreshNModal(false)}
        message="Data will be refreshed nightly only if the assistant is published."
      />
    </div>
  );
};

export default FileUploader;